/**
 * Recursive, controlled inline editor for an extraction schema's fields.
 *
 * Lets a member edit each field directly — name, description, required flag,
 * type, and the type's constraints (max length, min/max/unit, enum choices,
 * date format) — plus add / remove fields and recurse into nested "Table"
 * (object_list) fields. Optional per-field AI refinement (sparkle) when
 * ``editing`` is true and ``onAiRefineField`` is provided.
 */
import React, { useState } from "react"
import { SchemaAiRefineButton, SchemaAiRefinePanel } from "./SchemaAiRefine"
import { fieldPathLabel } from "./schemaFieldPath"

export const TYPE_KINDS = [
  { id: "text", label: "Text" },
  { id: "integer", label: "Integer" },
  { id: "number", label: "Number" },
  { id: "choice", label: "Choice" },
  { id: "date", label: "Date" },
  { id: "boolean", label: "Yes / No" },
  { id: "table", label: "Table (nested)" },
]

export const DATE_FORMATS = ["AAAA-MM-JJ", "AAAA-MM-JJTHH:MM:SS,ss-/+FF:ff"]

export function typeKind(objectType) {
  const t = objectType || {}
  if (t.object_list) return "table"
  if (t.string) return "text"
  if (t.integer) return "integer"
  if (t._float) return "number"
  if (t.enum) return "choice"
  if (t.date) return "date"
  if (t.boolean) return "boolean"
  return "text"
}

export function makeObjectType(kind, prev) {
  const p = prev || {}
  switch (kind) {
    case "text":
      return { string: "string", string_maxLength: p.string_maxLength ?? null }
    case "integer":
      return {
        integer: "integer",
        integer_minimum: p.integer_minimum ?? null,
        integer_maximum: p.integer_maximum ?? null,
        integer_unit: p.integer_unit ?? null,
      }
    case "number":
      return {
        _float: "_float",
        number_minimum: p.number_minimum ?? null,
        number_maximum: p.number_maximum ?? null,
        number_unit: p.number_unit ?? null,
      }
    case "choice":
      return { enum: "enum", enumeration_choices: Array.isArray(p.enumeration_choices) ? p.enumeration_choices : [] }
    case "date":
      return { date: "date", date_format: DATE_FORMATS.includes(p.date_format) ? p.date_format : DATE_FORMATS[0] }
    case "boolean":
      return { boolean: "boolean" }
    case "table":
      return { object_list: "object_list", fields: Array.isArray(p.fields) ? p.fields : [] }
    default:
      return { string: "string", string_maxLength: null }
  }
}

export function blankField() {
  return {
    object_name: "",
    object_description: "",
    object_required: false,
    object_type: { string: "string", string_maxLength: null },
  }
}

const inputCls =
  "w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
const labelCls = "block text-[11px] font-medium uppercase tracking-wide text-gray-500"

function numOrNull(v) {
  if (v === "" || v == null) return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

function NumberField({ label, value, onChange, placeholder }) {
  return (
    <label className="block">
      <span className={labelCls}>{label}</span>
      <input
        type="number"
        value={value ?? ""}
        placeholder={placeholder}
        onChange={(e) => onChange(numOrNull(e.target.value))}
        className={inputCls}
      />
    </label>
  )
}

function TextField({ label, value, onChange, placeholder }) {
  return (
    <label className="block">
      <span className={labelCls}>{label}</span>
      <input
        type="text"
        value={value ?? ""}
        placeholder={placeholder}
        onChange={(e) => {
          const v = e.target.value
          onChange(v === "" ? null : v)
        }}
        className={inputCls}
      />
    </label>
  )
}

function TypeConstraints({ kind, objectType, onTypeChange }) {
  const t = objectType || {}
  const patch = (p) => onTypeChange({ ...t, ...p })

  if (kind === "text") {
    return (
      <div className="grid grid-cols-2 gap-2">
        <NumberField label="Max length" value={t.string_maxLength} placeholder="none" onChange={(v) => patch({ string_maxLength: v })} />
      </div>
    )
  }
  if (kind === "integer") {
    return (
      <div className="grid grid-cols-3 gap-2">
        <NumberField label="Min" value={t.integer_minimum} placeholder="—" onChange={(v) => patch({ integer_minimum: v })} />
        <NumberField label="Max" value={t.integer_maximum} placeholder="—" onChange={(v) => patch({ integer_maximum: v })} />
        <TextField label="Unit" value={t.integer_unit} placeholder="e.g. years" onChange={(v) => patch({ integer_unit: v })} />
      </div>
    )
  }
  if (kind === "number") {
    return (
      <div className="grid grid-cols-3 gap-2">
        <NumberField label="Min" value={t.number_minimum} placeholder="—" onChange={(v) => patch({ number_minimum: v })} />
        <NumberField label="Max" value={t.number_maximum} placeholder="—" onChange={(v) => patch({ number_maximum: v })} />
        <TextField label="Unit" value={t.number_unit} placeholder="e.g. °C" onChange={(v) => patch({ number_unit: v })} />
      </div>
    )
  }
  if (kind === "choice") {
    const choices = Array.isArray(t.enumeration_choices) ? t.enumeration_choices : []
    return (
      <label className="block">
        <span className={labelCls}>Choices (comma-separated)</span>
        <input
          type="text"
          value={choices.join(", ")}
          placeholder="e.g. infection, bleeding, other"
          onChange={(e) =>
            patch({
              enumeration_choices: e.target.value
                .split(",")
                .map((s) => s.trim())
                .filter((s) => s.length > 0),
            })
          }
          className={inputCls}
        />
      </label>
    )
  }
  if (kind === "date") {
    return (
      <label className="block">
        <span className={labelCls}>Date format</span>
        <select
          value={DATE_FORMATS.includes(t.date_format) ? t.date_format : DATE_FORMATS[0]}
          onChange={(e) => patch({ date_format: e.target.value })}
          className={inputCls}
        >
          <option value={DATE_FORMATS[0]}>Date only (AAAA-MM-JJ)</option>
          <option value={DATE_FORMATS[1]}>Date + time (ISO 8601)</option>
        </select>
      </label>
    )
  }
  return null
}

function pathKey(path) {
  return (path || []).join(".")
}

function FieldEditor({
  field,
  onChange,
  onRemove,
  depth,
  dupName,
  fieldPath,
  rootFields,
  editing,
  onAiRefineField,
  aiRefineState,
}) {
  const f = field || {}
  const kind = typeKind(f.object_type)
  const update = (patch) => onChange({ ...f, ...patch })
  const setType = (objectType) => onChange({ ...f, object_type: objectType })
  const nameEmpty = !String(f.object_name || "").trim()
  const pk = pathKey(fieldPath)
  const aiOpen = aiRefineState?.openPath === pk
  const aiBusy = aiRefineState?.busyPath === pk
  const aiResult = aiRefineState?.resultPath === pk ? aiRefineState?.result : null
  const aiError = aiRefineState?.errorPath === pk ? aiRefineState?.error : null

  return (
    <div className={`relative rounded-lg border border-amber-200 bg-white p-3 ${aiBusy ? "opacity-90" : ""}`}>
      {aiBusy && (
        <div className="pointer-events-none absolute inset-0 z-10 rounded-lg bg-white/40" aria-hidden="true" />
      )}
      <div className="flex flex-wrap items-end gap-2">
        <label className="min-w-[10rem] flex-1">
          <span className={labelCls}>Field name</span>
          <input
            type="text"
            value={f.object_name ?? ""}
            onChange={(e) => update({ object_name: e.target.value })}
            placeholder="snake_case_name"
            disabled={aiBusy}
            className={`${inputCls} font-mono ${nameEmpty || dupName ? "border-red-300 focus:border-red-500 focus:ring-red-500" : ""}`}
          />
        </label>
        <label className="w-40">
          <span className={labelCls}>Type</span>
          <select
            value={kind}
            onChange={(e) => setType(makeObjectType(e.target.value, f.object_type))}
            disabled={aiBusy}
            className={inputCls}
          >
            {TYPE_KINDS.map((k) => (
              <option key={k.id} value={k.id}>
                {k.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-1.5 pb-1.5 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={!!f.object_required}
            onChange={(e) => update({ object_required: e.target.checked })}
            disabled={aiBusy}
            className="h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
          />
          required
        </label>
        {editing && onAiRefineField ? (
          <SchemaAiRefineButton
            compact
            busy={aiBusy}
            title={`Refine “${f.object_name || fieldPathLabel(rootFields, fieldPath)}” with AI`}
            onClick={() => onAiRefineField(fieldPath, f, aiOpen ? "close" : "open")}
          />
        ) : null}
        <button
          type="button"
          onClick={onRemove}
          disabled={aiBusy}
          title="Remove this field"
          aria-label={`Remove field ${f.object_name || ""}`}
          className="ml-auto rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-500 hover:border-red-300 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
        >
          Remove
        </button>
      </div>

      {(nameEmpty || dupName) && (
        <p className="mt-1 text-xs text-red-600">
          {nameEmpty ? "Field name is required." : "Duplicate field name in this sheet."}
        </p>
      )}

      <div className="mt-2">
        <label className="block">
          <span className={labelCls}>Description</span>
          <input
            type="text"
            value={f.object_description ?? ""}
            onChange={(e) => update({ object_description: e.target.value })}
            placeholder="What this field captures"
            disabled={aiBusy}
            className={inputCls}
          />
        </label>
      </div>

      {kind !== "boolean" && kind !== "table" && (
        <div className="mt-2">
          <TypeConstraints kind={kind} objectType={f.object_type} onTypeChange={setType} />
        </div>
      )}

      {editing && onAiRefineField ? (
        <SchemaAiRefinePanel
          open={aiOpen}
          scope="field"
          loading={aiBusy}
          result={aiResult}
          error={aiError}
          onSubmit={(instruction) => onAiRefineField(fieldPath, f, "submit", instruction)}
          onApply={() => onAiRefineField(fieldPath, f, "apply")}
          onDiscard={() => onAiRefineField(fieldPath, f, "discard")}
          onClose={() => onAiRefineField(fieldPath, f, "close")}
        />
      ) : null}

      {kind === "table" && (
        <div className="mt-3 rounded-md border border-blue-100 bg-blue-50/40 p-2">
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-blue-700">
            Nested fields of “{f.object_name || "table"}”
          </div>
          <SchemaFieldsEditor
            fields={Array.isArray(f.object_type?.fields) ? f.object_type.fields : []}
            onChange={(nextFields) => setType({ object_list: "object_list", fields: nextFields })}
            depth={depth + 1}
            parentPath={fieldPath}
            rootFields={rootFields}
            editing={editing}
            onAiRefineField={onAiRefineField}
            aiRefineState={aiRefineState}
          />
        </div>
      )}
    </div>
  )
}

export default function SchemaFieldsEditor({
  fields,
  onChange,
  depth = 0,
  parentPath = [],
  rootFields = null,
  editing = false,
  onAiRefineField = null,
  aiRefineState = null,
}) {
  const list = Array.isArray(fields) ? fields : []
  const roots = rootFields || list
  const setAt = (i, next) => onChange(list.map((x, j) => (j === i ? next : x)))
  const removeAt = (i) => onChange(list.filter((_, j) => j !== i))
  const add = () => onChange([...list, blankField()])

  const nameCounts = list.reduce((acc, f) => {
    const n = String(f?.object_name || "").trim().toLowerCase()
    if (n) acc[n] = (acc[n] || 0) + 1
    return acc
  }, {})

  return (
    <div className="space-y-2">
      {list.map((f, i) => {
        const n = String(f?.object_name || "").trim().toLowerCase()
        const fieldPath = [...parentPath, i]
        return (
          <FieldEditor
            key={pathKey(fieldPath)}
            field={f}
            depth={depth}
            fieldPath={fieldPath}
            rootFields={roots}
            editing={editing}
            onAiRefineField={onAiRefineField}
            aiRefineState={aiRefineState}
            dupName={!!n && nameCounts[n] > 1}
            onChange={(next) => setAt(i, next)}
            onRemove={() => removeAt(i)}
          />
        )
      })}
      <button
        type="button"
        onClick={add}
        className="w-full rounded-md border border-dashed border-amber-300 px-3 py-1.5 text-sm font-medium text-amber-700 hover:bg-amber-50"
      >
        + Add field
      </button>
    </div>
  )
}

export function validateFields(fields) {
  const list = Array.isArray(fields) ? fields : []
  const seen = new Set()
  for (const f of list) {
    const n = String(f?.object_name || "").trim().toLowerCase()
    if (!n) return false
    if (seen.has(n)) return false
    seen.add(n)
    if (typeKind(f?.object_type) === "table") {
      if (!validateFields(f.object_type?.fields)) return false
    }
  }
  return true
}
